// Global native file-input bridge.
//
// Inside the Capacitor shell, the WebView's own `<input type="file">` sheet is
// inconsistent (no camera on some Android OEMs, no multi-select on older iOS).
// Instead of touching all ~27 upload surfaces, we intercept clicks on file
// inputs once, ask the user for a source, run the native picker and assign the
// resulting `File` objects back onto the input via `DataTransfer` so every
// existing `onChange` handler keeps working unchanged.
//
// On the web this module registers nothing.

import { isNativeApp } from "./platform";
import { acceptIsImageOnly, pickNativeFiles, type NativePickSource } from "./media";

export const NATIVE_PICKER_REQUEST_EVENT = "hibane:native-picker-request";

export type NativePickerRequest = {
  imageOnly: boolean;
  multiple: boolean;
  accept?: string;
  /** Called with the chosen source, or null when the user cancelled. */
  resolve: (source: NativePickSource | null) => void;
};

/** Marker so a programmatic re-dispatch isn't intercepted again. */
const BYPASS = "__hibaneNativeBypass";

const assignFiles = (input: HTMLInputElement, files: File[]) => {
  const dt = new DataTransfer();
  files.forEach((f) => dt.items.add(f));
  input.files = dt.files;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
};

const askSource = (req: Omit<NativePickerRequest, "resolve">) =>
  new Promise<NativePickSource | null>((resolve) => {
    let settled = false;
    const detail: NativePickerRequest = {
      ...req,
      resolve: (source) => {
        if (settled) return;
        settled = true;
        resolve(source);
      },
    };
    const dispatched = window.dispatchEvent(
      new CustomEvent<NativePickerRequest>(NATIVE_PICKER_REQUEST_EVENT, { detail, cancelable: true }),
    );
    // If nothing handled the event (sheet not mounted) fall back to the
    // system file picker rather than dead-ending the upload.
    if (dispatched && !settled) {
      settled = true;
      resolve(null);
    }
  });

/**
 * Install the interceptor. Returns a cleanup function.
 * Safe to call on any platform.
 */
export function initNativeFileInputBridge(): () => void {
  if (!isNativeApp()) return () => {};

  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const input = target.closest?.('input[type="file"]') as HTMLInputElement | null;
    if (!input || input.disabled) return;
    if ((input as unknown as Record<string, unknown>)[BYPASS]) {
      delete (input as unknown as Record<string, unknown>)[BYPASS];
      return;
    }

    const accept = input.getAttribute("accept") || undefined;
    const imageOnly = acceptIsImageOnly(accept);
    const multiple = input.multiple;

    event.preventDefault();
    event.stopPropagation();

    void (async () => {
      const source = await askSource({ imageOnly, multiple, accept });
      if (!source) {
        // Cancelled, or no sheet mounted → let the WebView handle it natively.
        return;
      }
      try {
        const files = await pickNativeFiles(source, { accept, multiple });
        if (files.length) assignFiles(input, files);
      } catch (err) {
        // User cancelling the native picker throws — treat as a no-op.
        console.warn("[native] file pick cancelled/failed", err);
      }
    })();
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

/** Open the OS file dialog for an input, bypassing this bridge once. */
export const openSystemFilePicker = (input: HTMLInputElement) => {
  (input as unknown as Record<string, unknown>)[BYPASS] = true;
  input.click();
};
